import type { MathCheckQualification } from './mathCheckQualification.js';
/** Public Math Check contract. Frozen answers and generator provenance stay server-side. */
import { validExamLevelsForOrgLevel, type ExamLevel } from './examLevels.js';
import {
  isMathSubjectApplicable,
  MATH_SUBJECTS,
  type MathSubject,
} from './mathSubjects.js';
import {
  ORG_LEVELS,
  orgLevelDisplayLabel,
  type OrgLevel,
} from './orgLevels.js';
import type { MathDiagram } from './diagrams/types.js';

export const MATH_CHECK_COUNT = 5;
export const MATH_CHECK_WINDOW_MS = 24 * 60 * 60 * 1000;
export const MATH_CHECK_RETENTION_MS = 400 * MATH_CHECK_WINDOW_MS;
export interface MathCheckCohort {
  orgLevel: OrgLevel;
  examLevel: ExamLevel;
  mathSubject: MathSubject | null;
}
export const MATH_CHECK_COHORTS: readonly MathCheckCohort[] =
  ORG_LEVELS.flatMap(orgLevel =>
    validExamLevelsForOrgLevel(orgLevel).flatMap(examLevel =>
      (isMathSubjectApplicable(examLevel, orgLevel)
        ? MATH_SUBJECTS
        : [null]
      ).map(mathSubject => ({ orgLevel, examLevel, mathSubject }))
    )
  );
export const mathCheckCohortKey = (c: MathCheckCohort): string =>
  [c.orgLevel, c.examLevel, c.mathSubject ?? ''].join('|');
export const mathCheckCohortLabel = (c: MathCheckCohort): string =>
  [
    orgLevelDisplayLabel(c.orgLevel),
    c.examLevel === 'PSLE' ? null : c.examLevel,
    c.mathSubject,
  ]
    .filter(Boolean)
    .join(' · ');
/** URL form of a cohort for the admin level page: `p6`, `sec1-ip`, `sec3-g2-emath`. */
export const mathCheckLevelSlug = (c: MathCheckCohort): string =>
  [
    c.orgLevel.replace('primary_', 'p').replace('secondary_', 'sec'),
    c.examLevel === 'PSLE' ? null : c.examLevel,
    c.mathSubject?.replace('-', ''),
  ]
    .filter(Boolean)
    .join('-')
    .toLowerCase();
export const mathCheckCohortFromSlug = (slug: string): MathCheckCohort | null =>
  MATH_CHECK_COHORTS.find(
    c => mathCheckLevelSlug(c) === slug.trim().toLowerCase()
  ) ?? null;
export const mathCheckPath = (slug: string): string =>
  '/math-check/' + encodeURIComponent(slug);
export const isMathCheckPath = (path: string): boolean =>
  /^\/math-check(?:\/|$)/i.test(path.split('?')[0]);
export interface MathCheckContent {
  type: 'plain' | 'latex' | 'mixed';
  text?: string;
  latex?: string;
}
export interface MathCheckQuestion {
  position: number;
  topic: string;
  stem: MathCheckContent;
  options: { key: string; text: MathCheckContent }[];
  diagram?: MathDiagram;
}
export interface MathCheckAnswerReview {
  position: number;
  /** The skill (knowledge-graph node name) the question practises; null when it has none. */
  skill: string | null;
  chosenKey: string | null;
  correct: boolean;
  answerKey: string;
  explanation: MathCheckContent;
  steps: MathCheckContent[];
  diagram?: MathDiagram;
}
export interface MathCheckResult {
  score: number;
  total: 5;
  reviews: MathCheckAnswerReview[];
  completedAt: string;
}
/** A partner's five colours. The page always sets its type in Sora. */
export interface MathCheckBrand {
  accent: string;
  accentText: string;
  background: string;
  surface: string;
  text: string;
}
// Partner colour tokens are explicitly configurable; these are conservative starter values.
export const DEFAULT_MATH_CHECK_BRAND: MathCheckBrand = {
  accent: '#14688F',
  accentText: '#FFFFFF',
  background: '#F1F9FD',
  surface: '#FFFFFF',
  text: '#26303A',
};
/** The five colours of a stored or pinned brand; a missing or malformed colour takes its starter value. */
export function mathCheckBrand(raw: unknown): MathCheckBrand {
  const stored = (raw ?? {}) as Partial<Record<keyof MathCheckBrand, unknown>>;
  const colour = (key: keyof MathCheckBrand) => {
    const value = stored[key];
    return typeof value === 'string' && /^#[a-f\d]{6}$/i.test(value)
      ? value
      : DEFAULT_MATH_CHECK_BRAND[key];
  };
  return {
    accent: colour('accent'),
    accentText: colour('accentText'),
    background: colour('background'),
    surface: colour('surface'),
    text: colour('text'),
  };
}
export function mathCheckContrast(a: string, b: string): number {
  const luminance = (hex: string) => {
    const rgb = [1, 3, 5]
      .map(i => parseInt(hex.slice(i, i + 2), 16) / 255)
      .map(v => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
    return rgb[0] * 0.2126 + rgb[1] * 0.7152 + rgb[2] * 0.0722;
  };
  const x = luminance(a),
    y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}
export interface MathCheckPageConfig {
  brand: MathCheckBrand;
  enabled: boolean;
  displayName: string;
  logoUrl: string;
  privacyPolicyUrl?: string;
  intro: string;
}
/** An optional external policy link. Empty means the partner has no extra policy. */
export function isMathCheckPrivacyPolicyUrl(value: string): boolean {
  if (value === '') return true;
  if (value.length > 2000 || !/^https:\/\//i.test(value)) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password;
  } catch {
    return false;
  }
}
export interface MathCheckPage {
  qualification?: MathCheckQualification;
  brand: MathCheckBrand;
  partnerSlug: string;
  displayName: string;
  logoUrl: string;
  privacyPolicyUrl?: string;
  intro: string;
  cohorts: MathCheckCohort[];
}
export interface MathCheckRun {
  token: string;
  page: Omit<MathCheckPage, 'cohorts'>;
  partnerSlug: string;
  partnerName: string;
  cohort: MathCheckCohort;
  questions: MathCheckQuestion[];
  result: MathCheckResult | null;
  expiresAt: string;
  leadSubmitted: boolean;
  existingContact?: boolean;
}
export interface MathCheckLeadSummary {
  sessionId: string;
  versionId: string;
  cohort: MathCheckCohort;
  score: number;
  total: 5;
  completedAt: string;
  topicsToPractise: string[];
}
export interface MathCheckDraftItem {
  questionId: string;
  seed: string;
  fingerprint: string;
}
export interface MathCheckAdminItem extends MathCheckDraftItem {
  question: MathCheckQuestion;
  /** The skill (knowledge-graph node name) the question practises; null when it has none. */
  skill: string | null;
  answerKey: string;
  explanation: MathCheckContent;
  steps: MathCheckContent[];
  solutionDiagram?: MathDiagram;
}
/** One level as the overview shows it. */
export interface MathCheckAdminSetSummary {
  cohort: MathCheckCohort;
  enabled: boolean;
  publishedAt: string | null;
  stoppedAt: string | null;
  draftCount: number;
  /** The saved draft differs from the published five. */
  changed: boolean;
  /** Released questions this level can choose from. */
  bankCount: number;
  starts: number;
  completions: number;
  trialRequests: number;
}
/** One level with its draft, for the level page. */
export interface MathCheckAdminSet extends MathCheckAdminSetSummary {
  revision: number;
  items: MathCheckAdminItem[];
  /** Content fingerprints of the published five in order; null before the first publish. */
  liveFingerprints: string[] | null;
}
export interface MathCheckBankPage {
  questions: MathCheckAdminItem[];
  hasMore: boolean;
  /** Questions matching the search and topic. */
  total: number;
  /** Every topic matching the search, with its count, whatever the topic filter. */
  topics: { topic: string; count: number }[];
}
